import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, Navigate } from 'react-router-dom';
import { portalMode } from '@/config/portal';
import { getApiErrorMessage } from '@/utils/apiError';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { authApi } from '@/api/auth';
import { apiClient } from '@/api/client';
import { cn } from '@/lib/utils';
import { localPhonePlaceholder, publishedSupportHref } from '@/utils/phone';

type LoginFormData = { mobile: string };

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loginError, setLoginError] = useState('');
  const { t } = useTranslation();
  const loginSchema = z.object({
    mobile: z.string().regex(/^\d{9,10}$/, t('auth.login.invalidPhone')),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [phonePrefix, setPhonePrefix] = useState('');
  const [supportHref, setSupportHref] = useState('');

  useEffect(() => {
    apiClient.get('/tenants/meta')
      .then((res) => {
        setSupportHref(publishedSupportHref(res.data));
        if (res.data?.phonePrefix) {
          setPhonePrefix(res.data.phonePrefix);
        }
      })
      .catch(() => {});
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const mobileValue = watch('mobile');

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginError('');
    try {
      await authApi.requestOtp({
        mobile: data.mobile,
        clientType: portalMode === 'provider' ? 'provider' : 'support',
      });

      sessionStorage.setItem('login_mobile', data.mobile);
      const next = searchParams.get('next');
      if (next === '/dispatcher' || next === '/field') sessionStorage.setItem('login_return', next);
      else sessionStorage.removeItem('login_return');
      toast.success(t('auth.otp.codeSent'));
      navigate('/verify-otp');
    } catch (error) {
      setLoginError(getApiErrorMessage(error, t('auth.login.loginError')));
    } finally {
      setIsLoading(false);
    }
  };

  if (portalMode === 'superadmin') return <Navigate to="/admin-login" replace />;
  const denied = searchParams.get('reason');
  const accessError = denied === 'provider-portal-disabled' ? t('auth.login.portalDisabled') : denied === 'portal-role-mismatch' ? t('auth.login.portalAccessDenied') : '';

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="text-center mb-2">
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('auth.login.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('auth.login.subtitle')}</p>
      </div>

      {(loginError || accessError) && <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{loginError || accessError}</p>}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Phone Input */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="login-mobile" className="text-sm font-medium text-foreground">
            {t('auth.login.phoneLabel')}
          </label>
          <div
            className={cn(
              "flex items-center rounded-md border transition-all overflow-hidden bg-background",
              isFocused ? "border-primary ring-1 ring-primary" : "border-input hover:border-border300",
              errors.mobile && "border-destructive ring-1 ring-destructive"
            )}
          >
            {/* Country Code */}
            <div className="flex items-center gap-2 px-3 py-2 bg-muted border-s border-input">
              <span className="text-muted-foreground font-medium text-sm">{phonePrefix}</span>
            </div>
            {/* Input */}
            <input
              id="login-mobile"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              aria-invalid={Boolean(errors.mobile)}
              aria-describedby={errors.mobile ? 'login-mobile-error' : undefined}
              placeholder={localPhonePlaceholder(phonePrefix, t('auth.login.phonePlaceholder'))}
              maxLength={10}
              className="flex-1 px-3 py-2 h-[38px] text-sm bg-transparent outline-none placeholder:text-muted-foreground"
              dir="ltr"
              onFocus={() => setIsFocused(true)}
              {...register('mobile', {
                onBlur: () => setIsFocused(false),
              })}
            />
            {/* Validation Icon */}
            {mobileValue && mobileValue.length >= 9 && !errors.mobile && (
              <div className="ps-2 pe-3">
                <ShieldCheck className="w-5 h-5 text-success" />
              </div>
            )}
          </div>
          {errors.mobile && (
            <p id="login-mobile-error" role="alert" className="text-xs text-destructive flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.mobile.message}
            </p>
          )}
        </div>

        {/* Login Button */}
        <Button
          type="submit"
          loading={isLoading}
          className="w-full justify-center"
        >
          {!isLoading && (
            <>
              {t('auth.login.loginButton')}
              <ArrowLeft className="w-4 h-4 me-1" />
            </>
          )}
        </Button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <span className="flex-1 border-t border-border" />
        <span className="text-xs text-muted-foreground uppercase font-medium">{t('common.or')}</span>
        <span className="flex-1 border-t border-border" />
      </div>

      {/* Admin Login Link */}
      <div className="text-center">
        <Link to="/admin-login" className="text-sm text-muted-foreground hover:text-primary">
          {t('auth.login.adminLogin')}
        </Link>
      </div>

      {/* Help */}
      {supportHref && <div className="text-center">
        <p className="text-sm text-muted-foreground">
          {t('auth.login.helpText')}{' '}
          <a
            href={supportHref}
            className="text-primary font-medium hover:underline underline-offset-2"
          >
            {t('auth.login.contactSupport')}
          </a>
        </p>
      </div>}
    </div>
  );
}
